import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import {
  listClients,
  listReservationTypes,
  createReservationType,
  updateReservationType,
  deleteReservationType,
} from "@/lib/api";
import { Tag, Plus, Pencil, Trash2 } from "lucide-react";

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

const BLANK = {
  name: "",
  code: "",
  description: "",
  default_duration_minutes: "",
  color: "#3b82f6",
  requires_approval: false,
  sort_order: 0,
  is_active: true,
};

/**
 * Administration > Reservation Types
 * Per-tenant categorisation (Delivery, Pickup, Maintenance, etc.) with
 * a default duration + colour pip. Surfaced in the New Booking form.
 */
export default function AdminReservationTypes() {
  const { hasRole, assignments } = useAuth();
  const { viewAsClientId } = useViewAsClient();

  const myClientIds = useMemo(
    () => assignments.filter((a) => a.client_id).map((a) => a.client_id),
    [assignments]
  );

  // For super-admin, default to first client unless they've view-as-selected one.
  // For client-admin, lock to their assigned client.
  const isSuper = hasRole(["super_admin"]);
  const [allClients, setAllClients] = useState([]);
  const [clientId, setClientId] = useState(viewAsClientId || myClientIds[0] || "");
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | row object

  // Load clients once. Use Promise.then() (not async iife) so the lint rule
  // `react-hooks/set-state-in-effect` isn't triggered.
  useEffect(() => {
    let cancelled = false;
    listClients().then(({ data }) => {
      if (cancelled) return;
      const rows = (data || []).filter(
        (c) => isSuper || myClientIds.includes(c.id)
      );
      setAllClients(rows);
      setClientId((prev) => prev || rows[0]?.id || "");
    });
    return () => { cancelled = true; };
  }, [isSuper, myClientIds]);

  // Load types whenever active client changes
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    (async () => {
      const { data, error } = await listReservationTypes({
        clientId,
        includeInactive: true,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setTypes(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const refresh = async () => {
    if (!clientId) return;
    const { data, error } = await listReservationTypes({
      clientId,
      includeInactive: true,
    });
    if (error) toast.error(error.message);
    setTypes(data || []);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="reservation-types-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5" /> Reservation Types
          </h1>
          <p className="text-sm text-slate-500">
            Categorise bookings (&quot;Delivery / Pickup / Maintenance / Inspection&quot;), each
            with its own default duration, colour pip, and approval rule.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {allClients.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Tenant</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="w-56" data-testid="client-picker">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allClients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            onClick={() => setEditing("new")}
            data-testid="add-reservation-type-btn"
            disabled={!clientId}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add type
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">All types</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <div className="p-4 text-sm text-slate-500">Loading…</div>}
          {!loading && types.length === 0 && (
            <Alert className="m-4">
              <AlertDescription>
                No reservation types yet. Click &quot;Add type&quot; to create the first one.
              </AlertDescription>
            </Alert>
          )}
          {!loading && types.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Colour</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Default duration</th>
                  <th className="text-left px-4 py-2">Approval</th>
                  <th className="text-left px-4 py-2">Active</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-t" data-testid={`type-row-${t.id}`}>
                    <td className="px-4 py-2">
                      <span
                        className="inline-block h-4 w-4 rounded-full border"
                        style={{ backgroundColor: t.color }}
                      />
                    </td>
                    <td className="px-4 py-2 font-medium">{t.name}</td>
                    <td className="px-4 py-2 text-slate-500">{t.code || "—"}</td>
                    <td className="px-4 py-2">
                      {t.default_duration_minutes ? `${t.default_duration_minutes} min` : "—"}
                    </td>
                    <td className="px-4 py-2">
                      {t.requires_approval ? "required" : "—"}
                    </td>
                    <td className="px-4 py-2">
                      {t.is_active ? <span className="text-green-700">yes</span> : <span className="text-slate-400">no</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(t)}
                        data-testid={`edit-type-${t.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={async () => {
                          if (!window.confirm(`Delete type "${t.name}"? Bookings using it will retain their existing label but lose the link.`)) return;
                          const { error } = await deleteReservationType(t.id);
                          if (error) toast.error(error.message);
                          else { toast.success("Deleted"); refresh(); }
                        }}
                        data-testid={`delete-type-${t.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ReservationTypeDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        clientId={clientId}
        row={editing && editing !== "new" ? editing : null}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    </div>
  );
}

function ReservationTypeDialog({ open, onOpenChange, clientId, row, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reservation-type-dialog">
        {open && (
          <ReservationTypeDialogInner
            key={row?.id || "new"}
            row={row}
            clientId={clientId}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReservationTypeDialogInner({ row, clientId, onOpenChange, onSaved }) {
  const isEdit = !!row;
  const [values, setValues] = useState(() => (row ? { ...BLANK, ...row } : { ...BLANK }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const hexValid = !values.color || HEX_RE.test(values.color);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!values.name?.trim()) return setErr("Name is required");
    if (!hexValid) return setErr("Colour must be a 6-digit hex like #3b82f6");
    const payload = { ...values, client_id: clientId };
    ["default_duration_minutes", "sort_order"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
        payload[k] = null;
      } else {
        const n = parseInt(payload[k], 10);
        payload[k] = Number.isFinite(n) ? n : null;
      }
    });
    setSaving(true);
    const { error } = isEdit
      ? await updateReservationType(row.id, payload)
      : await createReservationType(payload);
    setSaving(false);
    if (error) {
      setErr(error.message || JSON.stringify(error));
      return;
    }
    toast.success(isEdit ? "Type updated" : "Type created");
    onSaved?.();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit reservation type" : "New reservation type"}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
          {err && (
            <Alert variant="destructive">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Name *</Label>
              <Input
                value={values.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Delivery"
                data-testid="type-name-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Short code</Label>
              <Input
                value={values.code || ""}
                onChange={(e) => set("code", e.target.value)}
                placeholder="DEL"
                data-testid="type-code-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Default duration (min)</Label>
              <Input
                type="number"
                min="0"
                step="5"
                value={values.default_duration_minutes ?? ""}
                onChange={(e) => set("default_duration_minutes", e.target.value)}
                placeholder="e.g. 60"
                data-testid="type-duration-input"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Description</Label>
              <Input
                value={values.description || ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Optional — shown as tooltip on the booking form"
                data-testid="type-desc-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Colour</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={HEX_RE.test(values.color) ? values.color : "#3b82f6"}
                  onChange={(e) => set("color", e.target.value)}
                  className="h-9 w-12 rounded border bg-white cursor-pointer"
                  aria-label="Pick colour"
                  data-testid="type-color-picker"
                />
                <Input
                  value={values.color || ""}
                  onChange={(e) => set("color", e.target.value)}
                  className="font-mono"
                  placeholder="#3b82f6"
                  data-testid="type-color-hex"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={values.sort_order ?? 0}
                onChange={(e) => set("sort_order", e.target.value)}
                data-testid="type-sort-input"
              />
            </div>
            <div className="flex items-center justify-between rounded border bg-white px-3 py-2 col-span-1">
              <Label>Require approval</Label>
              <Switch
                checked={!!values.requires_approval}
                onCheckedChange={(v) => set("requires_approval", v)}
                data-testid="type-approval-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded border bg-white px-3 py-2 col-span-1">
              <Label>Active</Label>
              <Switch
                checked={values.is_active !== false}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="type-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="type-save-btn">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create type"}
            </Button>
          </DialogFooter>
        </form>
    </>
  );
}
